/**
 * Zod schemas for the Toolbox access and role API (epic PI-SPEC-SW-E283).
 *
 * Covered routes:
 * - `GET    /api/v1/toolbox/access`                    the caller's roles, capabilities and personas per project
 * - `PUT    /api/v1/toolbox/access/:project/persona`   choose the caller's persona on one project
 * - `GET    /api/v1/toolbox/roles?project=`            role assignments on one project
 * - `POST   /api/v1/toolbox/roles`                     assign a role on a project
 * - `DELETE /api/v1/toolbox/roles/:id`                 remove a role assignment
 *
 * `admin` never appears as a stored assignment: SpecTree derives it from
 * `User.isGlobalAdmin` and adds it to the caller's `roles` in the access
 * response. Request schemas are strict (unknown keys are refused). Response
 * schemas are lenient: unknown keys pass through, and role, capability and
 * persona values a newer SpecTree adds are dropped from lists rather than
 * failing the whole response. Every response is wrapped as `{ data: ... }`;
 * parse the envelope with `apiDataSchema` from the grooming module.
 */
import { z } from "zod";
import { isoDateTime } from "../zod-helpers.js";
import { isToolboxCapability, isToolboxPersona, isToolboxRole, PROJECT_ROLES, TOOLBOX_PERSONAS, TOOLBOX_ROLES, } from "./types.js";
// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------
/** Path of the caller's Toolbox access read. */
export const TOOLBOX_ACCESS_PATH = "/api/v1/toolbox/access";
/** Path prefix of the role-assignment routes. */
export const TOOLBOX_ROLES_PATH = "/api/v1/toolbox/roles";
/** Paths of the routes this module covers, relative to the SpecTree origin. */
export const toolboxApiPaths = {
    access: TOOLBOX_ACCESS_PATH,
    /** `PUT`: the caller's persona on one tracker project. */
    persona: (project) => `${TOOLBOX_ACCESS_PATH}/${encodeURIComponent(project)}/persona`,
    /** `GET` with `?project=` lists, `POST` assigns. */
    roles: TOOLBOX_ROLES_PATH,
    /** Role assignments of one project. */
    projectRoles: (project) => `${TOOLBOX_ROLES_PATH}?project=${encodeURIComponent(project)}`,
    /** `DELETE`: remove one role assignment. */
    roleAssignment: (assignmentId) => `${TOOLBOX_ROLES_PATH}/${encodeURIComponent(assignmentId)}`,
};
// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------
/** An ISO 8601 timestamp as JSON carries a Date. */
const isoDateTimeSchema = isoDateTime({ offset: true });
/** A tracker project (the ADO project name). */
const projectNameSchema = z.string().trim().min(1).max(256);
export const toolboxRoleSchema = z.enum(TOOLBOX_ROLES);
export const projectRoleSchema = z.enum(PROJECT_ROLES);
export const toolboxPersonaSchema = z.enum(TOOLBOX_PERSONAS);
/** A response list of roles; values this contract does not know are dropped. */
const roleListSchema = z
    .array(z.string())
    .transform((values) => values.filter(isToolboxRole));
/** A response list of capabilities; values this contract does not know are dropped. */
const capabilityListSchema = z
    .array(z.string())
    .transform((values) => values.filter(isToolboxCapability));
/** A response list of personas; values this contract does not know are dropped. */
const personaListSchema = z
    .array(z.string())
    .transform((values) => values.filter(isToolboxPersona));
// ---------------------------------------------------------------------------
// Access (GET /access, PUT /access/:project/persona)
// ---------------------------------------------------------------------------
/**
 * The caller's access on one tracker project:
 * - `roles`: their roles there, plus `admin` when they are a global admin;
 * - `capabilities`: the union of those roles' capabilities;
 * - `personas`: the personas they may choose from (empty for a Viewer);
 * - `persona`: the one currently in use there, or null when no role carries one.
 */
export const toolboxProjectAccessSchema = z
    .object({
    tracker: z.string(),
    /** The tracker project (the ADO project name). */
    container: z.string(),
    roles: roleListSchema,
    capabilities: capabilityListSchema,
    personas: personaListSchema,
    persona: z
        .string()
        .nullable()
        .transform((value) => (isToolboxPersona(value) ? value : null)),
})
    .loose();
/**
 * `GET /api/v1/toolbox/access` → `data`. `projects` lists every project the
 * caller holds a role on; a global admin holds `admin` everywhere, so
 * `isAdmin` tells pages to treat projects not listed as fully open.
 */
export const toolboxAccessResponseSchema = z
    .object({
    isAdmin: z.boolean(),
    projects: z.array(toolboxProjectAccessSchema),
})
    .loose();
/**
 * `PUT /api/v1/toolbox/access/:project/persona`: choose the persona the tools
 * use for the caller on that project. 403 `TOOLBOX_PERSONA_NOT_ALLOWED` when
 * the caller's roles there do not allow it. Response: the project's access.
 */
export const putPersonaBodySchema = z
    .object({
    persona: toolboxPersonaSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Role assignments (/roles)
// ---------------------------------------------------------------------------
/** One stored role assignment: a person, a project and one per-project role. */
export const toolboxRoleAssignmentSchema = z
    .object({
    id: z.string(),
    userId: z.string(),
    userName: z.string().nullable(),
    userEmail: z.string(),
    tracker: z.string(),
    /** The tracker project (the ADO project name). */
    container: z.string(),
    role: projectRoleSchema,
    assignedById: z.string().nullable(),
    assignedByName: z.string().nullable(),
    createdAt: isoDateTimeSchema,
})
    .loose();
/** `GET /api/v1/toolbox/roles?project=`: the project whose assignments to list. */
export const listRoleAssignmentsQuerySchema = z
    .object({
    project: projectNameSchema,
})
    .strict();
/** `GET /api/v1/toolbox/roles?project=` → `data`: the project's assignments. */
export const toolboxRoleAssignmentListResponseSchema = z.array(toolboxRoleAssignmentSchema);
/**
 * `POST /api/v1/toolbox/roles`: assign `role` to `userId` on `project`.
 * 403 `TOOLBOX_ROLE_ASSIGN_FORBIDDEN` when the caller may not assign that
 * role there (see `canAssignRole`). `admin` is refused by the schema.
 * Response: the created (or already existing) assignment.
 */
export const createRoleAssignmentBodySchema = z
    .object({
    userId: z.guid("userId must be a UUID"),
    project: projectNameSchema,
    role: projectRoleSchema,
})
    .strict();
//# sourceMappingURL=api.js.map